import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
	ArrowLeft,
	CircleAlert,
	Clock,
	ExternalLink,
	Gauge,
	Images,
	Settings2,
	TriangleAlert,
} from "lucide-react"
import {
	DetailPanel,
	DetailPanelSection,
	KeyValueItem,
	KeyValueList,
	PageHeader,
} from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
	FLUSH_INNER_PANEL_CLASS,
	RawReportPanel,
	SampleGrid,
	SCANNER_DETAIL_PAGE_CLASS,
	ScannerDetailSplit,
	WarningsPanel,
	WRAPPING_HEADER_ACTIONS,
} from "@/features/card-scanner/detail-blocks"
import { MetricGrid, numericMetrics } from "@/features/card-scanner/metrics"
import { ReasonText } from "@/features/card-scanner/reason-text"
import {
	formatDuration,
	formatInstant,
	Instant,
	MODEL_TYPE_LABELS,
	QueryError,
	StatusBadge,
	TERMINAL_JOB_STATUSES,
	useScannerGate,
} from "@/features/card-scanner/shared"
import { useMarketplaceOrpc } from "@/integrations/orpc/marketplace-react"

export const Route = createFileRoute("/card-scanner/jobs/$jobId")({
	component: JobDetailPage,
})

function JobDetailPage() {
	const allowed = useScannerGate()
	const { jobId } = Route.useParams()
	const { orpc, isAuthReady } = useMarketplaceOrpc()

	const { data, isLoading, isError, error } = useQuery({
		...orpc.cardScanner.getTrainingJob.queryOptions({ input: { jobId } }),
		enabled: allowed && isAuthReady,
		refetchInterval: (query) =>
			query.state.data &&
			!TERMINAL_JOB_STATUSES.includes(query.state.data.job.status)
				? 10_000
				: false,
	})
	const job = data?.job

	// The model this job minted, when it completed — for the cross-link.
	const modelsQuery = useQuery({
		...orpc.cardScanner.listModels.queryOptions({
			input: { type: job?.type },
		}),
		enabled: allowed && isAuthReady && job?.status === "done",
	})
	const mintedModel = modelsQuery.data?.models.find(
		(model) => model.sourceJobId === jobId,
	)

	if (!allowed) return null

	if (isLoading) {
		return (
			<div className={SCANNER_DETAIL_PAGE_CLASS}>
				<Skeleton className="h-10 w-96" />
				<Skeleton className="h-64 w-full" />
			</div>
		)
	}
	if (isError || !data || !job) {
		return (
			<div className={SCANNER_DETAIL_PAGE_CLASS}>
				<BackLink />
				<QueryError
					what="the training job"
					error={error ?? new Error("Job not found")}
				/>
			</div>
		)
	}

	const headline = numericMetrics(
		(data.resultMetadata?.metrics ?? null) as Record<string, unknown> | null,
	)
	const warnings = Array.isArray(data.resultMetadata?.warnings)
		? (data.resultMetadata?.warnings as unknown[])
		: []

	return (
		<div className={SCANNER_DETAIL_PAGE_CLASS}>
			<div className="flex flex-col gap-3">
				<BackLink />
				<PageHeader
					className={WRAPPING_HEADER_ACTIONS}
					title={
						<span className="inline-flex items-center gap-3">
							{MODEL_TYPE_LABELS[job.type]} training
							<StatusBadge status={job.status} />
						</span>
					}
					description={
						<span className="font-mono text-xs break-all">{job.id}</span>
					}
					actions={
						<>
							{data.logUrl && (
								<Button variant="outline" asChild>
									<a href={data.logUrl} target="_blank" rel="noreferrer">
										Training log <ExternalLink className="h-3.5 w-3.5" />
									</a>
								</Button>
							)}
							{mintedModel && (
								<Button asChild>
									<Link
										to="/card-scanner/models/$modelId"
										params={{ modelId: mintedModel.id }}
									>
										Model v{mintedModel.version}
									</Link>
								</Button>
							)}
						</>
					}
				/>
			</div>

			<ScannerDetailSplit
				aside={
					<DetailPanel title="Run" icon={Clock}>
						<DetailPanelSection label="Timing">
							<KeyValueList>
								<KeyValueItem label="Started">
									<Instant iso={job.startedAt} />
								</KeyValueItem>
								<KeyValueItem label="Duration">
									{formatDuration(job.startedAt, job.finishedAt)}
								</KeyValueItem>
								<KeyValueItem label="Heartbeat">
									{formatInstant(job.lastHeartbeatAt)}
								</KeyValueItem>
								<KeyValueItem label="Triggered by">
									{job.triggeredBy ?? "—"}
								</KeyValueItem>
							</KeyValueList>
						</DetailPanelSection>
						<DetailPanelSection label="Infrastructure">
							<KeyValueList>
								<KeyValueItem label="Instance" className="font-mono text-xs">
									{job.ec2InstanceId}
								</KeyValueItem>
								<KeyValueItem label="Image" className="font-mono text-xs">
									{job.imageTag}
								</KeyValueItem>
								<KeyValueItem label="Commit" className="font-mono text-xs">
									{job.gitCommit.slice(0, 12)}
								</KeyValueItem>
								<KeyValueItem
									label="S3 prefix"
									className="font-mono text-xs break-all"
								>
									{job.s3OutputPrefix}
								</KeyValueItem>
							</KeyValueList>
						</DetailPanelSection>
					</DetailPanel>
				}
			>
				{job.failureReason && (
					<DetailPanel title="Failure reason" icon={CircleAlert}>
						<DetailPanelSection className="bg-bad-soft text-bad">
							<ReasonText reason={job.failureReason} />
						</DetailPanelSection>
					</DetailPanel>
				)}

				{warnings.length > 0 && (
					<WarningsPanel
						noun="training"
						warnings={warnings}
						icon={TriangleAlert}
					/>
				)}

				{Object.keys(headline).length > 0 && (
					<DetailPanel title="Headline metrics" icon={Gauge} flush>
						<MetricGrid
							metrics={headline}
							className={FLUSH_INNER_PANEL_CLASS}
						/>
					</DetailPanel>
				)}

				{((job.params && Object.keys(job.params).length > 0) ||
					job.comment) && (
					<DetailPanel title="Dispatch" icon={Settings2}>
						{job.params && Object.keys(job.params).length > 0 && (
							<DetailPanelSection label="Params at dispatch">
								<pre className="overflow-auto font-mono text-xs">
									{JSON.stringify(job.params, null, 2)}
								</pre>
							</DetailPanelSection>
						)}
						{job.comment && (
							<DetailPanelSection label="Progress comments">
								<pre className="whitespace-pre-wrap break-words font-mono text-xs">
									{job.comment}
								</pre>
							</DetailPanelSection>
						)}
					</DetailPanel>
				)}

				<DetailPanel title="Eval samples" icon={Images} meta="output/eval/">
					<DetailPanelSection>
						{data.sampleUrls.length === 0 ? (
							<p className="text-muted-foreground">
								No samples uploaded, or the bucket is unreachable from this
								environment.
							</p>
						) : (
							<SampleGrid samples={data.sampleUrls} />
						)}
					</DetailPanelSection>
				</DetailPanel>

				{data.resultMetadata && (
					<RawReportPanel
						title="Raw run report"
						detail="run.json"
						data={data.resultMetadata}
						defaultOpen={!job.failureReason}
					/>
				)}
			</ScannerDetailSplit>
		</div>
	)
}

function BackLink() {
	return (
		<Link
			to="/card-scanner"
			className="inline-flex w-fit items-center gap-1 text-meta text-xs hover:text-foreground"
		>
			<ArrowLeft className="h-3.5 w-3.5" /> Training jobs
		</Link>
	)
}
